import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Plus,
  FileText,
  Settings,
  Copy,
  Trash2,
  Building2,
  Palette,
  Layers,
  Megaphone,
  Sparkles,
  ArrowRight,
} from "lucide-react";

/**
 * Premium, Apple-like Quote launcher.
 * Single hero CTA + opinionated template tiles.
 * Recent quotes appear below in a clean list.
 *
 * The legacy individual-calculator routes still work (admin/legacy access),
 * but they are no longer the primary surface here.
 */

const TEMPLATES = [
  { key: "blank", name: "Custom Quote", short: "Custom", tagline: "Start from scratch.", description: "Open the unified builder with no presets — pick exactly what you need.", template: null },
  { key: "presale_condo", name: "Pre-Sale Condo", short: "Pre-Sale Condo", tagline: "VEXOS + Inventory + Launch", description: "Full go-to-market for vertical developments.", template: "presale_condo" },
  { key: "townhome", name: "Townhome Dev", short: "Townhome Dev", tagline: "Renders + Tours + Brochure", description: "Streamlined package for horizontal communities.", template: "townhome" },
  { key: "investor_pitch", name: "Investor Pitch", short: "Investor Pitch", tagline: "Conceptual + OM + Deck", description: "Capital-raise ready.", template: "investor_pitch" },
  { key: "luxury_spec", name: "Luxury Spec", short: "Luxury Spec", tagline: "Renders + Cinematic + Social", description: "Flagship marketing assets.", template: "luxury_spec" },
  { key: "ai_content_studio", name: "AI Content", short: "AI Content", tagline: "AI Reels + Content Bundles", description: "AI-driven reels & social. Resold via TRP.", template: "ai_content" },
];

export default function CalculatorsDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const res = await apiClient.get("/calculators/quotes?limit=8");
      setRecentQuotes(res.data);
    } catch (err) {
      console.error("Failed to fetch quotes:", err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDuplicate(quoteId, e) {
    e.preventDefault();
    e.stopPropagation();
    try {
      const res = await apiClient.post(`/calculators/quotes/${quoteId}/duplicate`);
      toast.success("Quote duplicated");
      navigate(`/sales/calculators/${res.data.quote_type}/${res.data.id}`);
    } catch {
      toast.error("Failed to duplicate quote");
    }
  }

  async function handleDelete(quoteId, e) {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Delete this quote? This cannot be undone.")) return;
    try {
      await apiClient.delete(`/calculators/quotes/${quoteId}`);
      toast.success("Quote deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete quote");
    }
  }

  const handleCreate = (templateKey, routeOverride) => {
    if (routeOverride) {
      navigate(routeOverride);
      return;
    }
    const url = templateKey
      ? `/sales/calculators/combined/new?template=${templateKey}`
      : `/sales/calculators/combined/new`;
    navigate(url);
  };

  const statusColors = {
    draft: "bg-white/[0.04] text-[#C9C6C1] border-white/[0.08]",
    internal_review: "bg-white/[0.04] text-[#D5D3CE] border-white/[0.10]",
    sent: "bg-white/[0.06] text-[#D5D3CE] border-white/[0.12]",
    won: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    lost: "bg-red-500/10 text-red-400 border-red-500/20",
  };

  const quoteTypeIcons = {
    vexos: Building2,
    visualization: Palette,
    combined: Layers,
    marketing: Megaphone,
    collateral: Sparkles,
  };

  return (
    <div className="min-h-screen bg-[#000000] text-[#F4F2EE]" data-testid="calculators-dashboard">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-12">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783] mb-2">
              Quote Builder
            </div>
            <h1 className="text-[28px] sm:text-[40px] leading-tight font-light tracking-tight">
              Build a complete proposal.
              <br />
              <span className="text-[#8A8783] font-normal">In one place.</span>
            </h1>
          </div>
          {user?.role === "COO" && (
            <Link to="/sales/calculators/pricing-admin" className="w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto border-white/[0.10] bg-transparent text-[#C9C6C1] hover:bg-white/[0.06] hover:text-white gap-2"
                data-testid="pricing-admin-btn"
              >
                <Settings className="w-3.5 h-3.5" />
                Pricing Admin
              </Button>
            </Link>
          )}
        </div>

        {/* Primary CTA */}
        <button
          onClick={() => handleCreate(null)}
          className="group w-full rounded-3xl border border-white/[0.08] bg-[#121212] px-10 py-12 flex items-center justify-between transition-all duration-200 mb-10"
          data-testid="hero-create-quote"
        >
          <div className="text-left">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783] mb-2">
              Start fresh
            </div>
            <div className="text-[28px] font-light tracking-tight">
              <Plus className="w-7 h-7 inline-block mr-2 -mt-1" />
              Create New Quote
            </div>
            <div className="text-[14px] text-[#8A8783] mt-2 max-w-md">
              One unified builder for VEXOS, Visualization, Marketing, and Collateral.
            </div>
          </div>
          <ArrowRight className="w-7 h-7 text-[#565350] group-hover:text-white group-hover:translate-x-1 transition-all" />
        </button>

        {/* Quick Start Templates — compact horizontal strip */}
        <div className="mb-12">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783] mb-1">Quick Start Templates</div>
              <p className="text-[13px] text-[#8A8783]">Optional starting points for common deal types.</p>
            </div>
          </div>

          <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1 [scrollbar-width:thin]">
            {TEMPLATES.filter(t => t.key !== "blank").map((tpl) => (
              <button
                key={tpl.key}
                onClick={() => handleCreate(tpl.template, tpl.routeOverride)}
                className="group flex-shrink-0 text-left rounded-xl border border-white/[0.08] bg-[#000000] hover:border-white/[0.18] hover:bg-[#121212] px-4 py-3 transition-all duration-200 min-w-[200px]"
                data-testid={`template-${tpl.key}`}
              >
                <div className="text-[13px] font-semibold text-[#F4F2EE] leading-tight">{tpl.short || tpl.name}</div>
                <div className="text-[11px] text-[#8A8783] mt-1 leading-snug">{tpl.tagline}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Recent quotes */}
        <div>
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[#8A8783] mb-1">Continue working</div>
              <h2 className="text-[20px] font-light tracking-tight">Recent quotes</h2>
            </div>
            <Link to="/sales/calculators/saved">
              <Button variant="ghost" size="sm" className="text-[#C9C6C1] hover:text-white" data-testid="view-all-quotes">
                View all <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="text-center py-10 text-[#565350] text-sm">Loading…</div>
          ) : recentQuotes.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/[0.08] bg-transparent py-14 text-center">
              <FileText className="w-7 h-7 text-[#302E2B] mx-auto mb-3" />
              <div className="text-[15px] text-[#F4F2EE] font-medium">No quotes yet</div>
              <div className="text-[13px] text-[#8A8783] mt-1">Create your first quote to get started.</div>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/[0.08] bg-[#000000] overflow-hidden">
              {recentQuotes.map((quote, idx) => {
                const Icon = quoteTypeIcons[quote.quote_type] || FileText;
                return (
                  <Link
                    key={quote.id}
                    to={`/sales/calculators/${quote.quote_type}/${quote.id}`}
                    className={`flex items-center gap-4 px-5 py-4 hover:bg-white/[0.03] transition-colors ${idx > 0 ? "border-t border-white/[0.04]" : ""}`}
                    data-testid={`recent-quote-${quote.id}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-white/[0.04] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#8A8783]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] font-medium text-[#F4F2EE] truncate">
                          {quote.project_name || "Untitled"}
                        </span>
                        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${statusColors[quote.status] || statusColors.draft}`}>
                          {(quote.status || "draft").replace("_", " ")}
                        </Badge>
                      </div>
                      <div className="text-[12px] text-[#8A8783] mt-0.5 truncate">
                        {quote.client_name || "—"}
                        {quote.salesperson ? ` · ${quote.salesperson}` : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[14px] font-semibold text-[#F4F2EE] tabular-nums">
                        ${(quote.grand_total || 0).toLocaleString()}
                      </div>
                      <div className="text-[11px] text-[#565350] mt-0.5">
                        {new Date(quote.updated_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 ml-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleDuplicate(quote.id, e)}
                        title="Duplicate"
                        className="text-[#8A8783] hover:text-white hover:bg-white/[0.06] h-8 w-8"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleDelete(quote.id, e)}
                        title="Delete"
                        className="text-[#8A8783] hover:text-red-400 hover:bg-white/[0.06] h-8 w-8"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Legacy access — discreet footer */}
        <div className="mt-16 pt-8 border-t border-white/[0.04] text-center">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#302E2B] mb-2">Legacy access</div>
          <div className="flex items-center justify-center gap-1 flex-wrap">
            {[
              { path: "/sales/calculators/vexos/new", label: "VEXOS" },
              { path: "/sales/calculators/visualization/new", label: "Visualization" },
              { path: "/sales/calculators/marketing/new", label: "Marketing" },
              { path: "/sales/calculators/collateral/new", label: "Collateral" },
            ].map((l, i) => (
              <Link
                key={l.path}
                to={l.path}
                className="text-[11px] text-[#565350] hover:text-[#C9C6C1] px-2 transition-colors"
                data-testid={`legacy-${l.label.toLowerCase()}`}
              >
                {i > 0 && <span className="text-[#302E2B] mr-2">·</span>}
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
